import React from "react";
import { Icons } from "../common/DashboardComponents";
import StatusBadge from "../common/StatusBadge";

// Grievance status definitions
export const GRIEVANCE_STATUS = {
  RAISED: "1", // Grievance Raised By ESM
  ACCEPTED: "2", // Grievance Accepted By Aresa Center & Forwarded
  REJECTED: "3", // Grievance Rejected By Aresa Center
  IN_PROGRESS: "4", // In Progress
  AWAITING_DOCUMENTS: "5", // Awaiting Documents from ESM
  DOCUMENT_RESUBMITTED: "6", // Document Resubmitted by ESM
  COMPLETED: "7", // Completed
  CLOSED: "8", // Feedback submitted By ESM & Closed
  REOPENED: "9", // Re-opened by ESM
};

// Status options for filtering based on role
export const getStatusOptions = (role, grievances) => {
  // Common options available to all roles
  const commonOptions = [{ value: "all", label: "All Status" }];

  // Get unique statuses from grievances data
  const uniqueStatuses = [...new Set(grievances.map(g => g.STATUS_NAME).filter(Boolean))];

  // Create status options from unique statuses
  const statusOptions = uniqueStatuses.map(status => ({
    value: status,
    label: status
  }));

  return [...commonOptions, ...statusOptions];
};

// Helper function to get filtered grievances based on role
export const getFilteredGrievances = (role, grievances) => {
  switch (role) {
    case 2: // ARESA Center - show all grievances
      return grievances;
    case 3: // Branch - show only forwarded grievances
      return grievances;
    case 4: // DGAR - show all grievances
    case 5: // COLA - show all grievances
      return grievances;
    default:
      return grievances;
  }
};

// Get stats based on role and grievances
export const getGrievanceStats = (role, grievances) => {
  // Filter grievances based on role
  const filteredGrievances = getFilteredGrievances(role, grievances);

  // Calculate common stats
  const totalGrievances = filteredGrievances.length;
  const pendingGrievances = filteredGrievances.filter(
    (g) =>
      g.STATUS_NAME?.toLowerCase() === "pending" ||
      g.STATUS_ID === GRIEVANCE_STATUS.IN_PROGRESS
  ).length;
  const resolvedGrievances = filteredGrievances.filter(
    (g) =>
      g.STATUS_ID === GRIEVANCE_STATUS.COMPLETED ||
      g.STATUS_ID === GRIEVANCE_STATUS.CLOSED
  ).length;

  // Define stats cards based on role
  switch (role) {
    case 2: // ARESA Center
      return [
        {
          title: "Total Grievances",
          value: totalGrievances,
          icon: Icons.Grievances,
          color: "#3949ab",
          additionalInfo: `Pending: ${pendingGrievances} | Resolved: ${resolvedGrievances}`,
        },
      ];
    case 3: // Branch
      return [
        {
          title: "Total Grievances",
          value: totalGrievances,
          icon: Icons.Grievances,
          color: "#FF9800",
          additionalInfo: `Pending: ${pendingGrievances} | Resolved: ${resolvedGrievances}`,
        },
      ];
    case 4: // DGAR
    case 5:
    case 6:
    case 7:
    case 8: // COLA
      return [
        {
          title: "Total Grievances",
          value: totalGrievances,
          icon: Icons.Grievances,
          color: "#4CAF50",
          additionalInfo: `Pending: ${pendingGrievances} | Resolved: ${resolvedGrievances}`,
        },
      ];
    default:
      return [
        {
          title: "Grievances",
          value: totalGrievances,
          icon: Icons.Grievances,
          color: "#3949ab",
          additionalInfo: null,
        },
      ];
  }
};

// Get the title for the grievance list based on role
export const getGrievanceListTitle = (role) => {
  switch (role) {
    case 2:
      return "All Grievances";
    case 3:
      return "Branch Grievance List";
    case 4:
      return "DGAR Grievance List";
    case 5:
      return "COLA Grievance List";
    case 6:
      return "BRIG PERS Grievance List";
    case 7:
      return "ADG  Grievance List";
    case 8:
      return "DG Grievance List";

    default:
      return "Grievance List";
  }
};

// Get table columns based on role
export const getTableColumns = (role, navigate) => {
  // Common columns for all roles
  const commonColumns = [
    {
      header: "Grievance ID",
      render: (grievance) =>
        `G${grievance?.GRV_ID?.toString().padStart(10, "0")}`,
    },
    {
      header: "Applicant",
      render: (grievance) => grievance?.USER_NAME,
    },
    {
      header: "Category",
      render: (grievance) => grievance?.GRV_CAT,
    },
    {
      header: "Submission Date",
      render: (grievance) => grievance?.ENTRY_DATETIME,
    },
    {
      header: "Pending At",
      render: (grievance) => grievance?.PENDING_ROLE_NAME || "-",
    },
    {
      header: "Status",
      render: (grievance) => <StatusBadge status={grievance?.STATUS_NAME} />,
    },
    {
      header: "Actions",
      render: (grievance) => (
        <button
          className="btn btn-sm btn-primary"
          onClick={() => {
            navigate(`/GrievanceDetails`, {
              state: {
                id: grievance?.GRV_ID,
                centerName: grievance.UNIT_CENTRE_NAME || "N/A",
                esm: grievance.ESM_ID || "N/A",
                cat: grievance.GRV_CAT || "N/A",
                catId: grievance.GRV_CAT_ID || "",
                entryDate:grievance?.ENTRY_DATETIME
              },
            });
          }}
        >
          View Details
        </button>
      ),
    },
  ];

  // Add role-specific columns or modify the common columns
  if (role === 2) {
    // ARESA Center
    return [
      ...commonColumns.slice(0, 3), // ID, Applicant, Category
      {
        header: "Details",
        render: (grievance) => (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: "200px" }}
          >
            {grievance?.GRV_DETAILS || "N/A"}
          </span>
        ),
      },
      ...commonColumns.slice(3), // Date, Status, Actions
    ];
  } else if (role === 3) {
    // Branch
    return [
      ...commonColumns.slice(0, 3), // ID, Applicant, Category
      {
        header: "ARESA Center",
        render: (grievance) => grievance?.UNIT_CENTRE_NAME,
      },
      ...commonColumns.slice(3), // Date, Status, Actions
    ];
  } else {
    // DGAR (4) or COLA (5)
    return [
      ...commonColumns.slice(0, 3), // ID, Applicant, Category
      {
        header: "ARESA Center",
        render: (grievance) => grievance?.UNIT_CENTRE_NAME,
      },
      {
        header: "Branch Remarks",
        render: (grievance) => (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: "200px" }}
          >
            {grievance?.LAST_STATUS_REMARKS || "N/A"}
          </span>
        ),
      },
      ...commonColumns.slice(3), // Date, Status, Actions
    ];
  }
};
